<script>
import Modal from "@/components/g-modal.vue";
import Field from "@/components/input-v.vue";
import { StatusBusinessPlanEnum, TipoVPLEnum } from "@/core/enums/ativacaoped";
import { FieldTypeEnum } from "@/core/enums/field-types";
import {
  groupBy,
  isNil,
  isObject,
  isString,
  partition,
  toNumber
} from "lodash";
import moment from "moment";

const ComportamentoEnum = Object.freeze({
  SOMA: "soma",
  SUBTRAI: "subtrai"
});

const createEmptyValue = (competencias = []) => ({
  tipo_rubrica_vpl_id: null,
  comportamento: ComportamentoEnum.SOMA,
  ...competencias.reduce((acc, key) => ({ ...acc, [key]: null }), {})
});

const getValidNumber = str => {
  if (isNil(str)) {
    return [false, null];
  }

  let num = str;

  if (isString(str)) {
    const cleaned = str.replace(/\./g, "").replace(",", ".");
    num = toNumber(cleaned);
  }

  const isValid = isFinite(num) && !isNaN(num);

  return [isValid, num];
};

const comportamentoOptions = [
  {
    value: ComportamentoEnum.SOMA,
    label: "Somar"
  },
  {
    value: ComportamentoEnum.SUBTRAI,
    label: "Subtrair"
  }
];

export default {
  components: {
    Field,
    Modal
  },
  props: {
    amortizationEndDate: String,
    competencias: {
      type: Array,
      default: () => []
    },
    editMode: String,
    endDate: String,
    hasAdminRole: Boolean,
    /** @type RubricaVPL[] */
    projetados: {
      type: Array,
      default: () => []
    },
    /** @type RubricaVPL[] */
    realizados: {
      type: Array,
      default: () => []
    },
    rubricas: {
      type: Array,
      default: () => []
    },
    startDate: String,
    status: Number
  },
  data() {
    return {
      opened: false,
      selectedYear: moment().format("YYYY-MM"),
      value: createEmptyValue(this.competencias)
    };
  },
  computed: {
    alocacoes() {
      return this.editMode === "realizado" ? this.realizados : this.projetados;
    },
    disabledYearPagination() {
      const [first, last] = this.getYearLimits();
      return {
        prev: this.selectedYear <= first,
        next: this.selectedYear >= last
      };
    },
    groupedByYear() {
      return groupBy(this.competencias, competencia =>
        moment(competencia, "YYYY-MM").get("year")
      );
    },
    isExecutionPhase() {
      return (
        !this.isInitialPhase &&
        !this.isVersioningMode &&
        this.status !== StatusBusinessPlanEnum.REPROVADO
      );
    },
    isInitialPhase() {
      return [
        StatusBusinessPlanEnum.PREENCHIMENTO,
        StatusBusinessPlanEnum.APROVACAO
      ].includes(this.status);
    },
    isVersioningMode() {
      return [
        StatusBusinessPlanEnum.PREENCHIMENTO_NOVA_VERSAO,
        StatusBusinessPlanEnum.APROVACAO_NOVA_VERSAO
      ].includes(this.status);
    },
    labelTitle() {
      let label = "Retorno Financeiro";

      if (this.hasAdminRole) {
        label += " ou Custo";
      }

      return label;
    },
    modalTitle() {
      let label = "Alocação de valores " + (this.editMode === "realizado" ? "realizados" : "projetados") + " de Retorno Financeiro";

      if (this.hasAdminRole) {
        label += " ou Custo";
      }

      return label;
    },
    parsedDates() {
      const today = moment();
      const startDate = moment(this.startDate, "YYYY-MM-DD");
      const endDate = moment(this.endDate, "YYYY-MM-DD");
      const amortizationEndDate = moment(
        this.amortizationEndDate,
        "YYYY-MM-DD"
      );

      const custoStartDate = moment(startDate);
      const custoEndDate =
        !this.hasAdminRole &&
        (this.isExecutionPhase ||
          (this.isVersioningMode && this.editMode === "realizado")) &&
        today.isBefore(endDate, "month")
          ? today
          : endDate;

      const retornoStartDate = moment(endDate).add(1, "month");
      const retornoEndDate =
        !this.hasAdminRole &&
        (this.isExecutionPhase ||
          (this.isVersioningMode && this.editMode === "realizado")) &&
        today.isBefore(amortizationEndDate, "month")
          ? today
          : amortizationEndDate;

      const projetoIniciado = today.isSameOrAfter(startDate, "month");
      const amortizacaoIniciada = today.isAfter(endDate, "month");
      const canUpdateSelectedRubrica =
        this.hasAdminRole ||
        this.selectedRubrica?.tipo_id === TipoVPLEnum.CUSTO ||
        this.editMode === "projetado" ||
        amortizacaoIniciada;

      return {
        amortizacaoIniciada,
        canUpdateSelectedRubrica,
        custoEndDate,
        custoStartDate,
        projetoIniciado,
        retornoEndDate,
        retornoStartDate
      };
    },
    rubricaFields() {
      return [
        {
          key: "tipo_rubrica_vpl_id",
          name: `Retorno Financeiro${this.hasAdminRole ? " ou Custo" : ""}`,
          ajuda: "Apenas usuários FP&A podem alocar valores de custo 'People'.",
          type: FieldTypeEnum.AUTOCOMPLETE,
          rel: { to: this.rubricasOptions, key: "id", name: "descricao" },
          colSize: 12,
          rules: [{ rule: "required" }],
          valueChanged: this.onChangeSelection
        },
        {
          key: "comportamento",
          name: "O valor cadastrado deve:",
          ajuda:
            "Um valor negativo é uma exceção, usado apenas quando precisa registrar um desconto em uma categoria.",
          type: FieldTypeEnum.RADIO,
          rel: { to: comportamentoOptions, key: "value", name: "label" },
          colSize: 12,
          defaultValue: ComportamentoEnum.SOMA,
          rules: [{ rule: "required" }]
        }
      ];
    },
    rubricasOptions() {
      if (!Array.isArray(this.rubricas)) {
        return [];
      }

      const skipRubricas = ["outras despesas"];
      const filtered = this.rubricas
        .filter(
          ({ descricao }) =>
            !skipRubricas.includes(descricao?.toLowerCase() || "")
        )
        .sort(({ descricao: a }, { descricao: b }) => a?.localeCompare(b));
      const [retornos, custos] = partition(
        filtered,
        ({ tipo_id }) => tipo_id === TipoVPLEnum.RETORNO
      );

      if (retornos.length > 0 && custos.length > 0) {
        return [
          { header: "Retorno Financeiro" },
          ...retornos,
          { divider: true },
          { header: "Custo" },
          ...custos
        ];
      }

      if (retornos.length > 0) {
        return retornos;
      }

      if (custos.length > 0) {
        return custos;
      }

      return [];
    },
    selectedRubrica() {
      if (isNil(this.value.tipo_rubrica_vpl_id)) {
        return null;
      }

      return this.rubricas.find(
        ({ id }) => id === this.value.tipo_rubrica_vpl_id
      );
    },
    windows() {
      const editMode = this.editMode;
      const isInitialPhase = this.isInitialPhase;
      const {
        amortizacaoIniciada,
        custoEndDate,
        custoStartDate,
        projetoIniciado,
        retornoEndDate,
        retornoStartDate
      } = this.parsedDates;

      return Object.entries(this.groupedByYear).map(([year]) => ({
        value: year,
        fields: Array.from({ length: 12 }, (_, index) => {
          const month = `${index + 1}`.padStart(2, "0");
          const key = `${year}-${month}`;
          const date = moment(key, "YYYY-MM");

          const semRubricaSelecionada = isNil(this.selectedRubrica);
          const mesCustoValido =
            (this.hasAdminRole ||
              projetoIniciado ||
              editMode === "projetado") &&
            date.isSameOrAfter(custoStartDate, "month") &&
            date.isSameOrBefore(custoEndDate, "month");
          const mesRetornoValido =
            (this.hasAdminRole ||
              isInitialPhase ||
              amortizacaoIniciada ||
              editMode === "projetado") &&
            date.isSameOrAfter(retornoStartDate, "month") &&
            date.isSameOrBefore(retornoEndDate, "month");
          const disabled =
            semRubricaSelecionada ||
            (this.selectedRubrica?.tipo_id === TipoVPLEnum.CUSTO
              ? !mesCustoValido
              : !mesRetornoValido);

          return {
            key,
            name: moment(key, "YYYY-MM").format("MM/YYYY"),
            type: FieldTypeEnum.MONEY3,
            colSize: 3,
            disabled,
            block: false,
            valid: null
          };
        })
      }));
    },
    yearsOptions() {
      return Object.keys(this.groupedByYear)
        .sort()
        .map(year => ({
          text: year,
          value: year
        }));
    }
  },
  mounted() {
    window.addEventListener("keydown", this.handleGlobalPast);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleGlobalPast);
  },
  methods: {
    changeSelectedYear(amount = 1) {
      const year = parseInt(this.selectedYear);
      this.selectedYear = `${year + amount}`;
    },
    close() {
      this.opened = false;
    },
    getYearLimits() {
      const years = Array.isArray(this.yearsOptions)
        ? this.yearsOptions.map(({ value }) => value)
        : [];
      const first = years.shift();
      const last = years.pop() ?? first;
      return [first, last];
    },
    async handleGlobalPast(event) {
      if (!this.opened) {
        return;
      }

      const { ctrlKey, metaKey, key } = event;

      if ((ctrlKey || metaKey) && key === "v") {
        const activeElement = document.activeElement;
        const isInput =
          activeElement && activeElement.tagName.toLowerCase() === "input";

        if (!isInput) {
          event.preventDefault();
        }

        const monthField = this.$refs.monthFields?.find(field =>
          field.$el.contains(activeElement)
        );
        const competencia =
          monthField?.label.key ||
          this.windows[0].fields.find(({ disabled }) => !disabled)?.key ||
          this.endDate;
        const clipboardData = await navigator.clipboard.readText();
        const separator = clipboardData.trim().includes("\t") ? "\t" : ";";
        const isSingleValue =
          !clipboardData.trim().includes("\t") &&
          !clipboardData.trim().includes(";");

        if (isInput && isSingleValue) {
          return;
        }

        const values =
          clipboardData
            .replace(/[\r\n R$]/g, "")
            .replace(/\./g, "")
            .replace(/,/g, ".")
            .split(separator) || [];
        values.forEach((value, index) => {
          const key = moment(competencia, "YYYY-MM")
            .add(index, "month")
            .format("YYYY-MM");
          const parsedValue = parseFloat(value);
          this.value[key] = isNaN(parsedValue) ? null : parsedValue;
        });

        this.$forceUpdate(); // Vue não detecta a alteração do value, então tem que forçar renderização manualmente
      }
    },
    onChangeSelection(rubricaId) {
      let nextValue = createEmptyValue(this.competencias);
      let hasNegativeValue = false;
      const alocacao = this.alocacoes.find(
        ({ rubrica_id }) => rubrica_id === rubricaId
      );
      const competencias = alocacao?.competencias;

      if (isObject(competencias) && !isNil(competencias)) {
        Object.entries(competencias).forEach(([key, val]) => {
          const [isValid, num] = getValidNumber(val);

          if (isValid) {
            hasNegativeValue = hasNegativeValue || num < 0;
            nextValue[key] = Math.abs(num);
          }
        });
      }

      nextValue.tipo_rubrica_vpl_id = rubricaId;
      nextValue.comportamento = hasNegativeValue
        ? ComportamentoEnum.SUBTRAI
        : ComportamentoEnum.SOMA;
      this.value = nextValue;
    },
    open(rubricaId = null) {
      if (this.opened) {
        return;
      }

      if (!this.rubricas.length) {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "alert",
          title: "Alocação não disponível",
          text:
            "Não há retornos financeiros ou custos que possam ser atribuídos manualmente vinculados a este Business Plan."
        });
        return;
      }

      this.value = createEmptyValue(this.competencias);

      if (rubricaId) {
        this.onChangeSelection(rubricaId);
      }

      const [first] = this.getYearLimits();
      this.selectedYear = first;
      this.opened = true;
    },
    save() {
      const isInvalid = this.rubricaFields.some(
        ({ valid }) => !valid || !valid()
      );

      if (isInvalid) {
        return;
      }

      if (isNil(this.value.tipo_rubrica_vpl_id)) {
        this.notify({
          error:
            "Erro ao tentar alocar valores. Não foi possível recuperar o identificador do retorno financeiro. Se o erro persistir contacte o suporte."
        });
        this.close();
        return;
      }

      const body = this.competencias.reduce((acc, month) => {
        let value =
          month in this.value && !isNil(this.value[month])
            ? Math.abs(this.value[month])
            : null;

        if (this.value.comportamento === ComportamentoEnum.SUBTRAI) {
          value *= -1;
        }

        if (!isNil(value)) {
          return [
            ...acc,
            {
              tipo_rubrica_vpl_id: this.value.tipo_rubrica_vpl_id,
              competencia: month,
              valor: parseFloat(value)
            }
          ];
        }

        return acc;
      }, []);

      if (!body.length) {
        this.$notify({
          group: "geral",
          duration: 5000,
          type: "error",
          title: "Nenhuma competência informada",
          text:
            "Informe ao menos o valor de uma competência antes de continuar."
        });
        return;
      }

      this.$emit("save", body, this.close);
    }
  }
};
</script>

<template>
  <Modal
    appendTitleIcon="mdi-currency-usd"
    :opened.sync="opened"
    :title="modalTitle"
    width="600px"
  >
    <v-row class="ml-n4">
      <template v-for="field of rubricaFields">
        <Field
          v-model="value[field.key]"
          :key="field.key"
          :block.sync="field.block"
          :colSize="field.colSize"
          class="px-2"
          :label="field"
          :valid.sync="field.valid"
          @input="field.valueChanged"
        />
      </template>

      <v-col cols="12">
        <div
          v-if="
            value.tipo_rubrica_vpl_id && parsedDates.canUpdateSelectedRubrica
          "
          class="year-selection"
        >
          <v-btn
            icon
            color="primary"
            :disabled="disabledYearPagination.prev"
            @click="changeSelectedYear(-1)"
          >
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>

          <v-select
            v-model="selectedYear"
            dense
            :disabled="!yearsOptions.length"
            hide-details
            :items="yearsOptions"
            outlined
            append-icon=""
            :menu-props="{ contentClass: 'vpl-form-modal-selection-year-menu' }"
          ></v-select>

          <v-btn
            icon
            color="primary"
            :disabled="disabledYearPagination.next"
            @click="changeSelectedYear(1)"
          >
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>

        <p v-if="!value.tipo_rubrica_vpl_id" class="mt-3 mb-0">
          Selecione um Retorno Financeiro ou Custo para alocar valores.
        </p>
        <p v-else-if="!parsedDates.canUpdateSelectedRubrica" class="mt-3 mb-0">
          Não é possível alocar valores realizados de Retorno Financeiro antes
          do início do período de amortização.
        </p>
        <p v-else class="mt-3 mb-0">
          Preencha os campos abaixo ou cole (Ctrl + V) uma linha de uma planilha
          Excel para preencher os valores.
        </p>
      </v-col>

      <v-col
        v-if="value.tipo_rubrica_vpl_id && parsedDates.canUpdateSelectedRubrica"
        cols="12"
        class="px-0"
      >
        <v-window v-model="selectedYear" continuous>
          <v-window-item
            v-for="{ value: window, fields } in windows"
            :key="window"
            :value="window"
          >
            <v-row class="ma-0">
              <Field
                ref="monthFields"
                v-for="field in fields"
                v-model="value[field.key]"
                :block.sync="field.block"
                class="dynamic-field px-2"
                :colSize="field.colSize"
                :disabled="field.disabled"
                :key="field.key"
                :label="field"
                :valid.sync="field.valid"
              />
            </v-row>
          </v-window-item>
        </v-window>
      </v-col>
    </v-row>

    <template #buttons>
      <v-spacer />
      <v-btn class="px-5 ml-3" color="secondary" depressed @click="close">
        Cancelar
      </v-btn>
      <v-btn class="px-5 ml-3" color="primary" dark depressed @click="save">
        Salvar
      </v-btn>
    </template>
  </Modal>
</template>

<style lang="scss" scoped>
.dynamic-field::v-deep {
  .v-label-input {
    padding-left: 0 !important;

    & > span {
      width: 100%;
      text-align: center;
    }
  }

  .v-input.v-input--is-disabled .v-input__slot {
    background-color: rgba(0, 0, 0, 0.15);
  }
}

.v-text-field.v-select {
  max-width: min-content;

  &::v-deep {
    .v-select__selection--comma {
      margin: 4px;
    }
  }
}

.year-selection {
  display: flex;
  flex-direction: row;
  width: fit-content;
  margin: 0 auto;
}
</style>

<style lang="scss">
.vpl-form-modal-selection-year-menu {
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
